import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { observer } from 'mobx-react';
import { Alert, AlertTitle, Button, CircularProgress, Divider, FlexBox, IconCheckCircle, Skeleton, styled, Typography } from 'ml-rubiks-styleguide';
import { Stepper } from 'ml-rubiks-styleguide/src/dialog/stepper';
import { injectTSDI } from 'ml-webclient-common/src/tsdi';
import { BookingEntries } from './components/booking-entries';
import { MemberInfo } from './components/member-info';
import { PaymentMethodDetails } from './components/payment-method-details';
import { PaymentSummary } from './components/payment-summary';
import { PaymentWidgetWrapper } from './components/payment-widget-wrapper';
import { I18nStore } from './core/i18n';
import { resolveScenarioKeys } from './core/scenario-messages';
import { FastFinanceLinkStore } from './store';
const LoadingView = observer(function LoadingView() {
    const store = injectTSDI(FastFinanceLinkStore);
    const i18n = injectTSDI(I18nStore);
    let message;
    if (i18n.isReady) {
        if (store.isProcessingPayment) {
            message =
                store.currentFlow === 'PAYMENT'
                    ? i18n.__('ffl.processing.payment')
                    : i18n.__('ffl.processing.payment.method');
        }
        else {
            message = i18n.__('ffl.loading');
        }
    }
    return (_jsxs(FlexBox, { stack: true, gap: 2, hAlign: "center", vAlign: "center", sx: { minHeight: 200, p: 3 }, children: [_jsx(CircularProgress, { size: 24 }), message && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: message }))] }));
});
function PaymentFlowSkeleton() {
    return (_jsxs(FlexBox, { stack: true, gap: 3, sx: { p: { xs: 2.5, sm: 3, md: 4 } }, children: [_jsxs(FlexBox, { stack: true, gap: 1, children: [_jsx(Skeleton, { variant: "text", width: 260, height: 32 }), _jsx(Skeleton, { variant: "text", width: "80%", height: 20 })] }), _jsxs(FlexBox, { stack: true, children: [_jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40 }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40, sx: { mt: 0.5 } }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40, sx: { mt: 0.5 } }), _jsx(Skeleton, { variant: "rectangular", width: "100%", height: 40, sx: { mt: 0.5 } })] }), _jsxs(FlexBox, { stack: true, gap: 0.5, hAlign: "flex-end", children: [_jsx(Skeleton, { variant: "text", width: 220, height: 20 }), _jsx(Skeleton, { variant: "text", width: 220, height: 20 }), _jsx(Skeleton, { variant: "text", width: 220, height: 20 })] }), _jsx(FlexBox, { hAlign: "flex-end", children: _jsx(Skeleton, { variant: "rectangular", width: 140, height: 36 }) })] }));
}
function PaymentMethodUpdateSkeleton() {
    return (_jsxs(FlexBox, { stack: true, gap: 3, sx: { p: { xs: 2.5, sm: 3, md: 4 } }, children: [_jsxs(FlexBox, { stack: true, gap: 1, children: [_jsx(Skeleton, { variant: "text", width: 300, height: 32 }), _jsx(Skeleton, { variant: "text", width: "80%", height: 20 })] }), _jsxs(FlexBox, { stack: true, gap: 1, children: [_jsx(Skeleton, { variant: "text", width: "60%", height: 20 }), _jsx(Skeleton, { variant: "text", width: "60%", height: 20 }), _jsx(Skeleton, { variant: "text", width: "60%", height: 20 })] }), _jsxs(FlexBox, { stack: true, gap: 1, children: [_jsx(Skeleton, { variant: "text", width: "60%", height: 20 }), _jsx(Skeleton, { variant: "text", width: "60%", height: 20 }), _jsx(Skeleton, { variant: "rectangular", width: 80, height: 24 })] }), _jsx(FlexBox, { hAlign: "flex-end", children: _jsx(Skeleton, { variant: "rectangular", width: 160, height: 36 }) })] }));
}
const ErrorView = observer(function ErrorView() {
    const store = injectTSDI(FastFinanceLinkStore);
    const i18n = injectTSDI(I18nStore);
    const errorMessage = store.initError?.message ??
        store.errorMessage ??
        i18n.__('ffl.error.generic');
    return (_jsx(FlexBox, { padding: 3, children: _jsxs(Alert, { severity: "error", children: [_jsx(AlertTitle, { children: i18n.__('ffl.error.title') }), errorMessage] }) }));
});
const PaymentFlowOverview = observer(function PaymentFlowOverview() {
    const store = injectTSDI(FastFinanceLinkStore);
    const i18n = injectTSDI(I18nStore);
    const { primaryKey, previewKey, footerKey } = resolveScenarioKeys(store.scenarioConfig, 'PAYMENT', !store.isLastFlow);
    return (_jsxs(FlexBox, { stack: true, gap: 3, sx: { p: { xs: 2.5, sm: 3, md: 4 } }, children: [_jsxs(FlexBox, { stack: true, gap: 1, children: [_jsx(Typography, { variant: "h5", fontWeight: "bold", children: i18n.__('ffl.pay.open.claims.title') }), _jsx(Typography, { variant: "body1", color: "text.secondary", children: i18n.__(primaryKey) }), previewKey && (_jsx(Typography, { variant: "body1", color: "text.secondary", children: i18n.__(previewKey) })), _jsx(Typography, { variant: "body1", color: "text.secondary", children: i18n.__(footerKey) })] }), _jsx(BookingEntries, {}), _jsx(PaymentSummary, {}), _jsx(FlexBox, { hAlign: "flex-end", children: _jsx(Button, { variant: "contained", onClick: () => store.proceedToAction(), disabled: !store.hasOpenClaims, children: i18n.__('ffl.pay.open.claims.continue') }) })] }));
});
const PaymentMethodOverview = observer(function PaymentMethodOverview() {
    const store = injectTSDI(FastFinanceLinkStore);
    const i18n = injectTSDI(I18nStore);
    const { primaryKey, previewKey, footerKey } = resolveScenarioKeys(store.scenarioConfig, 'PAYMENT_METHOD_UPDATE', !store.isLastFlow);
    return (_jsxs(FlexBox, { stack: true, gap: 3, sx: { p: { xs: 2.5, sm: 3, md: 4 } }, children: [_jsxs(FlexBox, { stack: true, gap: 1, children: [_jsx(Typography, { variant: "h5", fontWeight: "bold", children: i18n.__('ffl.update.payment.method.title') }), _jsx(Typography, { variant: "body1", color: "text.secondary", children: i18n.__(primaryKey) }), previewKey && (_jsx(Typography, { variant: "body1", color: "text.secondary", children: i18n.__(previewKey) })), _jsx(Typography, { variant: "body1", color: "text.secondary", children: i18n.__(footerKey) })] }), _jsx(MemberInfo, {}), _jsx(PaymentMethodDetails, {}), _jsxs(FlexBox, { gap: 2, hAlign: "flex-end", children: [store.canSkipPaymentMethodUpdate && (_jsx(Button, { variant: "outlined", onClick: store.skipPaymentMethodUpdate, children: i18n.__('commons.skip') })), _jsx(Button, { variant: "contained", onClick: () => store.proceedToAction(), children: i18n.__('ffl.update.payment.method.allowed') })] })] }));
});
const ActionStep = observer(function ActionStep() {
    const store = injectTSDI(FastFinanceLinkStore);
    const i18n = injectTSDI(I18nStore);
    const scope = store.currentFlow === 'PAYMENT' ? 'ECOM' : 'MEMBER_ACCOUNT';
    return (_jsxs(FlexBox, { stack: true, gap: 3, sx: { p: 3 }, children: [_jsx(PaymentWidgetWrapper, { scope: scope }), _jsx(FlexBox, { hAlign: "flex-start", children: _jsx(Button, { variant: "outlined", onClick: () => store.goBackToOverview(), children: i18n.__('ffl.update.payment.method.go.back') }) })] }));
});
const SuccessStep = observer(function SuccessStep() {
    const store = injectTSDI(FastFinanceLinkStore);
    const i18n = injectTSDI(I18nStore);
    const message = store.currentFlow === 'PAYMENT'
        ? i18n.__('ffl.success.payment')
        : i18n.__('ffl.success.payment.method');
    return (_jsxs(FlexBox, { stack: true, gap: 2, hAlign: "center", vAlign: "center", padding: 3, children: [_jsx(Alert, { severity: "success", iconAlign: "center", icon: _jsx(IconCheckCircle, {}), children: message }), !store.isLastFlow && (_jsx(FlexBox, { hAlign: "flex-end", width: "100%", children: _jsx(Button, { variant: "contained", onClick: store.proceedToNextFlow, children: store.nextFlow === 'PAYMENT'
                        ? i18n.__('ffl.success.continue.payment')
                        : i18n.__('ffl.success.continue.payment.method') }) }))] }));
});
const FlowContent = observer(function FlowContent() {
    const store = injectTSDI(FastFinanceLinkStore);
    switch (store.currentStep) {
        case 'SUCCESS':
            return _jsx(SuccessStep, {});
        case 'ERROR':
            return _jsx(ErrorView, {});
        case 'ACTION':
            return _jsx(ActionStep, {});
        default:
            break;
    }
    switch (store.currentFlow) {
        case 'PAYMENT':
            return _jsx(PaymentFlowOverview, {});
        case 'PAYMENT_METHOD_UPDATE':
            return _jsx(PaymentMethodOverview, {});
        default:
            return _jsx(LoadingView, {});
    }
});
function getFlowLabel(flow, i18n) {
    switch (flow) {
        case 'PAYMENT':
            return i18n.__('ffl.pay.open.claims.flow');
        case 'PAYMENT_METHOD_UPDATE':
            return i18n.__('ffl.update.payment.method.flow');
        default:
            return flow;
    }
}
const StepperBar = styled(FlexBox)(({ $bgColor, $textColor }) => ({
    ...($bgColor && { backgroundColor: $bgColor }),
    ...($textColor && { color: $textColor })
}));
const FlowStepper = observer(function FlowStepper() {
    const store = injectTSDI(FastFinanceLinkStore);
    const i18n = injectTSDI(I18nStore);
    if (store.flowOrder.length <= 1) {
        return null;
    }
    const currentIndex = store.currentFlow
        ? store.flowOrder.indexOf(store.currentFlow)
        : 0;
    const steps = store.flowOrder.map((flow) => ({
        label: getFlowLabel(flow, i18n)
    }));
    const bgColor = store.styling?.backgroundColor2;
    const textColor = store.styling?.textColor;
    return (_jsxs(_Fragment, { children: [_jsx(StepperBar, { stack: true, "$bgColor": bgColor, "$textColor": textColor, sx: { p: 3 }, children: _jsx(Stepper, { activeStep: currentIndex + 1, steps: steps }) }), _jsx(Divider, {})] }));
});
export const App = observer(function App() {
    const store = injectTSDI(FastFinanceLinkStore);
    if (!store.isInitialized) {
        return store.currentFlow === 'PAYMENT_METHOD_UPDATE' ? (_jsx(PaymentMethodUpdateSkeleton, {})) : (_jsx(PaymentFlowSkeleton, {}));
    }
    if (store.isProcessingPayment) {
        return _jsx(LoadingView, {});
    }
    if (store.hasError && !store.currentFlow) {
        return _jsx(ErrorView, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(FlowStepper, {}), _jsx(FlowContent, {})] }));
});
